/**
 * Batch-6 hrm — pure helpers voor de HR-trend-calculators.
 *
 * Twee soorten datums in de trend-bronnen:
 *  1. `WerkbonDayEntry.date` — UTC-middernacht waarvan de UTC-componenten de
 *     NL-kalenderdag ZIJN (day-entry-conventie). Gebruik
 *     `utcDagdatumNaarBucketKeys`.
 *  2. Echte instants (createdAt e.d.) — eerst naar de Europe/Amsterdam-
 *     kalenderdag converteren. Gebruik `instantNaarNlBucketKeys`.
 *
 * Week-buckets zijn ISO-weken (ma-zo, weeknummer 1-53) op de NL-kalenderdag.
 *
 * K-anonimiteit (verplicht voor leeftijdsopbouw + m/v-verdeling): cellen die
 * minder dan `k` (default 5) medewerkers vertegenwoordigen worden samengevoegd
 * tot één "Samengevoegd"-cel zodat een kleine BV-cel niet herleidbaar is tot
 * een persoon. Dit hoort IN de calculator te gebeuren — niet alleen in de UI.
 */

// ─── Kalenderdag → bucket-keys ───────────────────────────────────────────────

const NL_YMD_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Amsterdam',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

export interface TrendBucketKeys {
  /** yyyy-MM-dd */
  dag: string
  /** "YYYY-Www" — ISO-week (ma-zo) van de kalenderdag. */
  week: string
  /** yyyy-MM */
  maand: string
}

/** ISO-weeknummer + ISO-weekjaar voor een (y, m0, d)-kalenderdag. */
function isoWeekVanYmd(year: number, month0: number, day: number): { isoJaar: number; week: number } {
  // Standaard ISO-8601-algoritme op een UTC-datum met dezelfde componenten.
  const d = new Date(Date.UTC(year, month0, day))
  const dayNum = d.getUTCDay() === 0 ? 7 : d.getUTCDay() // ma=1 .. zo=7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum) // donderdag van deze ISO-week
  const isoJaar = d.getUTCFullYear()
  const jaarStart = new Date(Date.UTC(isoJaar, 0, 1))
  const week = Math.ceil(((d.getTime() - jaarStart.getTime()) / 86400000 + 1) / 7)
  return { isoJaar, week }
}

function ymdNaarKeys(year: number, month0: number, day: number): TrendBucketKeys {
  const { isoJaar, week } = isoWeekVanYmd(year, month0, day)
  const mm = String(month0 + 1).padStart(2, '0')
  const dd = String(day).padStart(2, '0')
  return {
    dag: `${year}-${mm}-${dd}`,
    week: `${isoJaar}-W${String(week).padStart(2, '0')}`,
    maand: `${year}-${mm}`,
  }
}

/** Bucket-keys voor een UTC-dagdatum (day-entry-conventie: UTC-componenten = NL-dag). */
export function utcDagdatumNaarBucketKeys(d: Date): TrendBucketKeys {
  return ymdNaarKeys(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
}

/** Bucket-keys voor een echt instant — kalenderdag in Europe/Amsterdam. */
export function instantNaarNlBucketKeys(d: Date): TrendBucketKeys {
  const [y, m, day] = NL_YMD_FMT.format(d).split('-').map(Number)
  return ymdNaarKeys(y as number, (m as number) - 1, day as number)
}

// ─── Leeftijd ────────────────────────────────────────────────────────────────

// Leeftijd zelf berekenen: gebruik `berekenLeeftijd(birthDate, peildatum)` uit
// `./dienstverband` (bestaande NL-tijd-helper) — hier alleen de bucket-indeling.

/** Volgorde + labels van de 5-jaars-leeftijdsbuckets. */
export const LEEFTIJD_BUCKETS = [
  '<25',
  '25-29',
  '30-34',
  '35-39',
  '40-44',
  '45-49',
  '50-54',
  '55-59',
  '60+',
] as const
export type LeeftijdBucket = (typeof LEEFTIJD_BUCKETS)[number]

export function leeftijdBucket(leeftijd: number): LeeftijdBucket {
  if (leeftijd < 25) return '<25'
  if (leeftijd >= 60) return '60+'
  const start = Math.floor(leeftijd / 5) * 5
  return `${start}-${start + 4}` as LeeftijdBucket
}

// ─── K-anonimiteit ───────────────────────────────────────────────────────────

export const K_ANONIMITEIT_DREMPEL = 5
export const K_ANONIMITEIT_SAMENGEVOEGD_ID = 'samengevoegd'
export const K_ANONIMITEIT_SAMENGEVOEGD_LABEL = 'Samengevoegd (cellen < 5)'

export interface AnonimiteitsCel {
  id: string
  label: string
  aantal: number
}

/**
 * Voeg cellen met minder dan `k` personen samen tot één "Samengevoegd"-cel.
 * De samengevoegde cel noemt de onderliggende categorieën bewust NIET — dat is
 * precies de afscherming. Cellen met 0 personen worden weggelaten (geen cel =
 * geen informatie). De volgorde van de overgebleven cellen blijft behouden;
 * de samengevoegde cel komt achteraan.
 *
 * De samengevoegde cel moet zélf ook >= k zijn: wie het totaal kent, kan een
 * kleinere restcel anders terugrekenen naar (bijna) één persoon ("Man: 6" +
 * "Samengevoegd: 1" = die ene collega is geen man). Haalt de restcel de
 * drempel niet, dan absorbeert hij de kleinste zichtbare cellen tot dat wel
 * zo is; blijft er niets zichtbaars over, dan resteert alleen het totaal en
 * bevat de cel geen attribuut-informatie meer.
 */
export function pasKAnonimiteitToe(
  cellen: AnonimiteitsCel[],
  k: number = K_ANONIMITEIT_DREMPEL,
): AnonimiteitsCel[] {
  const zichtbaar: AnonimiteitsCel[] = []
  let samengevoegd = 0
  for (const cel of cellen) {
    if (cel.aantal <= 0) continue
    if (cel.aantal < k) samengevoegd += cel.aantal
    else zichtbaar.push(cel)
  }
  while (samengevoegd > 0 && samengevoegd < k && zichtbaar.length > 0) {
    let kleinste = 0
    for (let i = 1; i < zichtbaar.length; i++) {
      if (zichtbaar[i].aantal < zichtbaar[kleinste].aantal) kleinste = i
    }
    samengevoegd += zichtbaar[kleinste].aantal
    zichtbaar.splice(kleinste, 1)
  }
  if (samengevoegd > 0) {
    zichtbaar.push({
      id: K_ANONIMITEIT_SAMENGEVOEGD_ID,
      label: K_ANONIMITEIT_SAMENGEVOEGD_LABEL,
      aantal: samengevoegd,
    })
  }
  return zichtbaar
}

// ─── Dienstjaren ─────────────────────────────────────────────────────────────

export const DIENSTJAREN_BUCKETS = ['0-1', '1-3', '3-5', '5-10', '10+'] as const
export type DienstjarenBucket = (typeof DIENSTJAREN_BUCKETS)[number]

export function dienstjarenBucket(jaren: number): DienstjarenBucket {
  if (jaren < 1) return '0-1'
  if (jaren < 3) return '1-3'
  if (jaren < 5) return '3-5'
  if (jaren < 10) return '5-10'
  return '10+'
}
